"use client"

import { useCallback, useEffect, useState } from "react"

/**
 * A creative the visitor picked, held as a blob: URL in this tab only.
 *
 * Both browser-only ad tools (safe-zone checker, mockup generator) promise the
 * file never leaves the device, so there is deliberately no fetch, no upload
 * and no persistence here — just `URL.createObjectURL`, revoked when the file
 * is replaced or the component unmounts.
 */
export interface LocalMedia {
  url: string
  kind: "image" | "video"
  name: string
  /** Natural pixel size, filled in once the browser has decoded the file. */
  width: number | null
  height: number | null
}

export function useLocalMedia() {
  const [media, setMedia] = useState<LocalMedia | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Keyed on the URL, not the object: recording dimensions replaces `media`
  // with the same URL, and that must not revoke the file still on screen.
  const url = media?.url
  useEffect(() => {
    return () => {
      if (url) URL.revokeObjectURL(url)
    }
  }, [url])

  const pick = useCallback((file: File | null | undefined) => {
    if (!file) return
    const kind = file.type.startsWith("image/")
      ? "image"
      : file.type.startsWith("video/")
        ? "video"
        : null
    if (!kind) {
      setError("Use an image (PNG, JPG, WebP) or a video (MP4, MOV, WebM).")
      return
    }
    setError(null)
    setMedia({
      url: URL.createObjectURL(file),
      kind,
      name: file.name,
      width: null,
      height: null,
    })
  }, [])

  const setDimensions = useCallback((width: number, height: number) => {
    setMedia(current =>
      current && (current.width !== width || current.height !== height)
        ? { ...current, width, height }
        : current,
    )
  }, [])

  const clear = useCallback(() => setMedia(null), [])

  // A MIME type only says what the file claims to be; the browser can still
  // fail to decode it (HEIC, an unsupported MOV codec, a corrupt file).
  const fail = useCallback(() => {
    setMedia(null)
    setError(
      "Your browser could not decode that file. Try a PNG, JPG or WebP image, or an H.264 MP4.",
    )
  }, [])

  return { media, error, pick, setDimensions, clear, fail }
}
